import { useMemo } from 'react';
import { mainLayoutRoutes } from '@/routes/config';
import { useLocation } from 'react-router-dom';

type RouteNode = {
  path?: string;
  index?: boolean;
  children?: RouteNode[];
};

type BreadcrumbEntry = {
  matcher: RegExp;
  crumbs: readonly string[];
  rootPath: string;
  parentPattern: string | null;
};

export type BreadcrumbResult = {
  crumbs: readonly string[];
  pageCrumb: string;
  rootPath: string;
  parentPath: string | null;
};

const IGNORE_SEGMENTS = new Set(['edit', 'create', 'detail', 'details']);

const CUSTOM_LABELS: Record<string, string> = {
  'bulk-imports': 'import_jobs',
};

const ORGANIZATION_CHILD_PATHS = new Set(
  mainLayoutRoutes
    .filter(r => r.parentNav === 'organization')
    .map(r => `/${r.path}`)
);

const formatSegment = (seg: string): string =>
  seg
    .replace(/:/g, '')
    .replace(/[-]/g, '_')

function buildBreadcrumbData(routes: readonly RouteNode[]) {
  const staticMap = new Map<string, BreadcrumbEntry>();
  const dynamicEntries: BreadcrumbEntry[] = [];

  const traverse = (
    routes: readonly RouteNode[],
    parentPath = '',
    parentCrumbs: readonly string[] = []
  ) => {
    for (const route of routes) {
      if (!route.path || route.index) continue;

      const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path;
      const segments = route.path.split('/').filter(Boolean);
      const crumbs: string[] = [...parentCrumbs];

      for (const seg of segments) {
        if (seg.startsWith(':')) { crumbs.push('detail'); continue; }
        if (IGNORE_SEGMENTS.has(seg)) continue;
        crumbs.push(CUSTOM_LABELS[seg] ?? formatSegment(seg));
      }

      const pathParts = fullPath.split('/');
      const rootPath = `/${pathParts[0]}`;
      // Strip last segment to get the parent path. Works for both nested routes
      // (parentPath populated by the tree traversal) and flat multi-segment routes
      // like `user-access/:adminId` where parentPath is empty but the parent is
      // derivable directly from the fullPath segments.
      const parentPattern = pathParts.length > 1
        ? pathParts.slice(0, -1).join('/')
        : null;

      const entry: BreadcrumbEntry = {
        matcher: new RegExp(`^/${fullPath.replace(/:[^/]+/g, '[^/]+')}$`),
        crumbs: Object.freeze(crumbs),
        rootPath,
        parentPattern,
      };

      if (fullPath.includes(':')) {
        dynamicEntries.push(entry);
      } else {
        staticMap.set(`/${fullPath}`, entry);
      }

      if (route.children) {
        traverse(route.children, fullPath, crumbs);
      }
    }
  };

  traverse(routes);

  // Sort by literal segment count: more literals = more specific.
  // This correctly ranks /assets/create above /assets/:id without length tricks.
  dynamicEntries.sort((a, b) => {
    const literalCount = (e: BreadcrumbEntry) =>
      e.matcher.source.split('/').filter(s => s && s !== '[^/]+').length;
    return literalCount(b) - literalCount(a);
  });

  return { staticMap, dynamicEntries };
}

// Built once at module load — O(1) static lookup, O(k) dynamic scan where k << n
const { staticMap, dynamicEntries } = buildBreadcrumbData(mainLayoutRoutes);

function resolveParentPath(parentPattern: string, pathname: string): string {
  const pathSegments = pathname.split('/').filter(Boolean);
  const resolved = parentPattern
    .split('/')
    .map((seg, i) => (seg.startsWith(':') ? (pathSegments[i] ?? seg) : seg))
    .join('/');
  return `/${resolved}`;
}

// Only expose parentPath if it resolves to an actual registered route.
// e.g. `asset-types/:id/settings` has parentPattern `asset-types/:id`
// which resolves to `/asset-types/<uuid>` — no route exists there.
function resolveRegisteredParentPath(
  parentPattern: string,
  pathname: string,
  fallback: string
): string {
  const resolved = resolveParentPath(parentPattern, pathname);
  const isRegistered =
    staticMap.has(resolved) ||
    dynamicEntries.some(e => e.matcher.test(resolved));
  return isRegistered ? resolved : fallback;
}

export function useBreadcrumbs(): BreadcrumbResult | null {
  const { pathname } = useLocation();

  return useMemo(() => {
    if (pathname === '/dashboard') return null;

    const entry =
      staticMap.get(pathname) ??
      dynamicEntries.find(e => e.matcher.test(pathname));

    if (!entry) return null;

    const isOrgChild = ORGANIZATION_CHILD_PATHS.has(entry.rootPath);

    const parentPath = entry.parentPattern
      ? resolveRegisteredParentPath(entry.parentPattern, pathname, entry.rootPath)
      : null;

    return {
      crumbs: isOrgChild
        ? (['organization', ...entry.crumbs] as const)
        : entry.crumbs,
      pageCrumb: entry.crumbs[0],
      rootPath: isOrgChild ? '/organization' : entry.rootPath,
      parentPath,
    };
  }, [pathname]);
}
